import React from "react";
import { Table, Tooltip, Badge } from "antd";
import dayjs from "dayjs";
import { Key } from "antd/es/table/interface";
import {
  Tariff,
  TariffTableRecord,
  TransportType,
  BookingType,
  ContainerSize,
} from "../utils/types";
import StatusTag from "../components/StatusTag";
import TariffActionButtons from "../components/TariffActionButtons";

interface TariffTableProps {
  tariffs: Tariff[];
  transportTypes: TransportType[];
  bookingTypes: BookingType[];
  containerSizes: ContainerSize[];
  loading: boolean;
  pageSize?: number;
  onEdit: (tariff: Tariff) => void;
  onDuplicate: (tariff: Tariff) => void;
  onDelete: (id: number) => void;
}

const TariffTable: React.FC<TariffTableProps> = ({
  tariffs,
  transportTypes,
  bookingTypes,
  containerSizes,
  loading,
  pageSize = 20,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const columns = [
    {
      title: "Period",
      key: "period",
      render: (_: unknown, record: TariffTableRecord) => (
        <>
          {record.from && record.to ? (
            <span>
              {dayjs(record.from).format("YYYY-MM-DD")} to{" "}
              {dayjs(record.to).format("YYYY-MM-DD")}
            </span>
          ) : record.from ? (
            <span>From {dayjs(record.from).format("YYYY-MM-DD")}</span>
          ) : record.to ? (
            <span>Until {dayjs(record.to).format("YYYY-MM-DD")}</span>
          ) : (
            <Tooltip title="No date range specified">
              <Badge status="warning" text="No dates" />
            </Tooltip>
          )}
        </>
      ),
      sorter: (a: TariffTableRecord, b: TariffTableRecord) => {
        const dateA = a.from ? dayjs(a.from) : dayjs(0);
        const dateB = b.from ? dayjs(b.from) : dayjs(0);
        return dateA.diff(dateB);
      },
    },
    {
      title: "Transport Type",
      dataIndex: ["transport_type_data", "name"],
      key: "transport_type",
      filters: transportTypes.map((type) => ({
        text: type.name,
        value: type.id,
      })),
      onFilter: (value: boolean | Key, record: TariffTableRecord) =>
        record.transport_type === value,
    },
    {
      title: "Route",
      key: "route",
      render: (_: unknown, record: TariffTableRecord) => (
        <span>
          {record.origin_port_data?.name || "Any"} →{" "}
          {record.destination_port_data?.name || "Any"}
        </span>
      ),
    },
    {
      title: "Booking Type",
      dataIndex: ["bookings_type_data", "name"],
      key: "bookings_type",
      filters: bookingTypes.map((type) => ({
        text: type.name,
        value: type.id,
      })),
      onFilter: (value: boolean | Key, record: TariffTableRecord) =>
        record.bookings_type === value,
    },
    {
      title: "Container Size",
      dataIndex: ["container_size_data", "name"],
      key: "container_size",
      filters: containerSizes.map((size) => ({
        text: size.name,
        value: size.id,
      })),
      onFilter: (value: boolean | Key, record: TariffTableRecord) =>
        record.container_size === value,
    },
    {
      title: "Bracket",
      key: "bracket",
      render: (_: unknown, record: TariffTableRecord) =>
        record.tariff_bracket_data ? (
          <span>
            {record.tariff_bracket_data.from} - {record.tariff_bracket_data.to}
          </span>
        ) : (
          "Any"
        ),
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number) =>
        price ? (
          <span>€{price.toFixed(2)}</span>
        ) : (
          <Tooltip title="No price specified">
            <Badge status="error" text="Missing" />
          </Tooltip>
        ),
      sorter: (a: TariffTableRecord, b: TariffTableRecord) => {
        const priceA = a.price || 0;
        const priceB = b.price || 0;
        return priceA - priceB;
      },
    },
    {
      title: "Status",
      key: "status",
      render: (_: unknown, record: TariffTableRecord) => (
        <StatusTag tariff={record} showTooltip />
      ),
      filters: [
        { text: "Active", value: "active" },
        { text: "Expired", value: "expired" },
        { text: "Future", value: "future" },
        { text: "Expiring Soon", value: "expiring" },
        { text: "Incomplete", value: "incomplete" },
      ],
      onFilter: (value: boolean | Key, record: TariffTableRecord): boolean => {
        const today = dayjs();
        const startDate = record.from ? dayjs(record.from) : null;
        const endDate = record.to ? dayjs(record.to) : null;
        const statusValue = value as string;

        if (statusValue === "incomplete") {
          return !startDate || !endDate || !record.price;
        }

        if (statusValue === "expired") {
          return endDate
            ? endDate.isBefore(today) && !endDate.isSame(today)
            : false;
        }

        if (statusValue === "future") {
          return startDate ? startDate.isAfter(today) : false;
        }

        if (statusValue === "expiring") {
          return (
            !!endDate &&
            endDate.isAfter(today) &&
            endDate.diff(today, "day") <= 30
          );
        }

        if (statusValue === "active") {
          return (
            !!startDate &&
            !!endDate &&
            startDate.isBefore(today) &&
            endDate.isAfter(today) &&
            endDate.diff(today, "day") > 30
          );
        }

        return false;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: TariffTableRecord) => (
        <TariffActionButtons
          tariff={record}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={tariffs}
      loading={loading}
      rowKey="id"
      pagination={{ pageSize }}
      scroll={{ x: "max-content" }}
    />
  );
};

export default TariffTable;
